/**
 * `KpiTile` — one top-row KPI box on the metrics dashboard.
 *
 * Anatomy:
 *   - mono uppercase kicker (label)
 *   - large JetBrains Mono value (the headline figure)
 *   - optional sub-stat (smaller mono text under the value — used for
 *     "approval rate 93.2%" under the Mandates tile, "median resolution
 *     12m" under Escalations, "net $1,234.56" under per-currency volume)
 *   - optional footnote (small italic-sans hint line; used for the
 *     honest "Live settlement volume — limited-production scope." note
 *     on the volume tile)
 *
 * Loading + error states render inline so the surrounding tile grid
 * keeps its shape (no layout shift between fetch states).
 */

import type { ReactNode } from 'react';

import { Card } from '../../components/atoms/Card.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";

export type KpiVariant = 'value' | 'loading' | 'error';

export interface KpiTileProps {
  kicker: string;
  value: ReactNode;
  /** Smaller mono line directly under the headline figure. */
  sub?: ReactNode;
  /** Italicised sans hint line at the foot of the tile. */
  footnote?: ReactNode;
  variant?: KpiVariant;
  /** Optional explicit min-height so a row of tiles aligns even when
   *  one tile has multiple per-currency rows. Caller-owned because the
   *  tile grid sets row height. */
  minHeight?: number;
}

export function KpiTile({
  kicker,
  value,
  sub,
  footnote,
  variant = 'value',
  minHeight,
}: KpiTileProps): JSX.Element {
  const C = useC();

  return (
    <Card
      style={{
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minHeight,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
        }}
      >
        {kicker}
      </div>
      {variant === 'loading' ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 14,
            color: C.dim,
            letterSpacing: 1,
            padding: '6px 0',
          }}
        >
          Loading…
        </div>
      ) : variant === 'error' ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.coralFg,
            letterSpacing: 1,
            padding: '6px 0',
          }}
        >
          Unavailable
        </div>
      ) : (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 26,
            lineHeight: 1.1,
            color: C.text,
            fontWeight: 500,
            letterSpacing: -0.5,
            wordBreak: 'break-word',
          }}
        >
          {value}
        </div>
      )}
      {sub !== undefined && variant === 'value' ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 0.5,
            lineHeight: 1.4,
          }}
        >
          {sub}
        </div>
      ) : null}
      {footnote !== undefined ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 10,
            color: C.dim,
            fontStyle: 'italic',
            marginTop: 'auto',
            paddingTop: 4,
            lineHeight: 1.4,
          }}
        >
          {footnote}
        </div>
      ) : null}
    </Card>
  );
}

/**
 * `SectionHeader` — a uniform "kicker + serif title + optional caption"
 * heading used by every section on the metrics view. Hoisted here so
 * the tile + chart + cohort sections share one shape.
 */
export interface SectionHeaderProps {
  kicker: string;
  title: ReactNode;
  caption?: ReactNode;
}

export function SectionHeader({ kicker, title, caption }: SectionHeaderProps): JSX.Element {
  const C = useC();
  return (
    <div className="mb-4">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        {kicker}
      </div>
      <h2
        style={{
          fontFamily: serifFont,
          fontSize: 24,
          color: C.text,
          letterSpacing: -0.8,
          lineHeight: 1.1,
          margin: 0,
        }}
      >
        {title}
      </h2>
      {caption !== undefined ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {caption}
        </div>
      ) : null}
    </div>
  );
}
